import { Button, Flex, message, Modal, TablePaginationConfig } from "antd";
import CreateModal from "./components/CreateModal";
import EditModal from "./components/EditModal";
import { getColumns } from "./utils/table";
import { ExpenseType } from "@/types/expenseType";
import { DEFAULT_PAGINATION } from "@/constants/table";
import { useMemo, useState } from "react";
import useModal from "@/hooks/useModal";
import {
  useGetExpenseTypes,
  useGetExpenseTypesOptions,
} from "../../hooks/useExpenseTypes";
import {
  useDeleteExpenseType,
  useRestoreExpenseType,
} from "../../hooks/useExpenseTypes";
import { FilterCondition } from "@/components/table/hooks/useFilter";
import { SortCondition } from "@/components/table/hooks/useSort";
import Table, { FilterOptions } from "@/components/table";

const ExpenseTypes = () => {
  const [sort, setSort] = useState<SortCondition | undefined>();
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] =
    useState<TablePaginationConfig>(DEFAULT_PAGINATION);

  const {
    isModalOpen: isAddModalOpen,
    openModal: openAddModal,
    closeModal: closeAddModal,
  } = useModal();
  const {
    modalData: editModalData,
    isModalOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal<ExpenseType>();

  const { mutate: deleteExpenseType } = useDeleteExpenseType();
  const { mutate: restoreExpenseType } = useRestoreExpenseType();
  const {
    data: expenseTypes,
    isFetching,
    refetch,
  } = useGetExpenseTypes({
    params: {
      include_inactive: true,
      sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
      filters: filters.length > 0 ? filters : undefined,
      limit: pagination?.pageSize,
      page: pagination?.current,
      offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 10),
    },
  });

  // Get filter options
  const { data: options } = useGetExpenseTypesOptions({
    params: {
      only: "name",
      limit: -1,
    },
  });

  const filterOptions = useMemo(
    () => ({
      name: (options || []).reduce((acc, item) => {
        if (item.name && !acc.some((option) => option.value === item?.name)) {
          acc.push({
            text: item.name,
            value: item.name,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  const handleToggleActive = (id: string, isActive: boolean) => {
    Modal.confirm({
      title: isActive ? "Activate Expense Type" : "Deactivate Expense Type",
      content: isActive
        ? "Are you sure you want to activate this expense type?"
        : "Are you sure you want to deactivate this expense type?",
      onOk: () => {
        if (isActive) {
          restoreExpenseType(
            { id },
            {
              onSuccess: () => {
                message.success("Successfully activated expense type");
                refetch();
              },
            }
          );
        } else {
          deleteExpenseType(
            { id },
            {
              onSuccess: () => {
                message.success("Successfully deactivated expense type");
                refetch();
              },
            }
          );
        }
      },
    });
  };

  // Disable worker access for now
  // const handleToggleAccessible = (id: string, expenseType: ExpenseType) => {
  //   const isAccessible = expenseType.worker_accessible;
  //   Modal.confirm({
  //     title: isAccessible ? "Enable Worker Access" : "Disable Worker Access",
  //     content: isAccessible
  //       ? "Are you sure you want to enable worker access to this expense type?"
  //       : "Are you sure you want to disable worker access to this expense type?",
  //     onOk: () => {
  //       const payload: UpdateExpenseTypeFormValues = {
  //         id,
  //         name: expenseType.name,
  //         description: expenseType.description || "",
  //         required_fields: expenseType.required_fields || [],
  //         // available_fields: expenseType.available_fields || [],
  //         worker_accessible: expenseType.worker_accessible,
  //       };

  //       updateExpenseType(payload, {
  //         onSuccess: () => {
  //           message.success(
  //             isAccessible
  //               ? "Successfully enabled worker access"
  //               : "Successfully disabled worker access"
  //           );
  //           refetch();
  //         },
  //       });
  //     },
  //   });
  // };

  // const columns = getColumns({
  //   onEdit: handleEdit,
  //   onToggleActive: handleToggleActive,
  //   // onToggleAccessible: handleToggleAccessible,
  // });

  return (
    <Flex vertical gap={16}>
      <Flex justify="flex-end" align="center">
        <Button type="primary" onClick={openAddModal}>
          New Type
        </Button>
      </Flex>

      <Table
        columns={(props) =>
          getColumns({
            ...props,
            onEdit: openEditModal,
            onToggleActive: handleToggleActive,
            filterOptions,
          })
        }
        dataSource={expenseTypes?.data || []}
        loading={isFetching}
        pagination={{
          defaultPagination: {
            ...DEFAULT_PAGINATION,
            showSizeChanger: true,
          },
          dataPagination: expenseTypes?.pagination,
          onChange: setPagination,
        }}
        scroll={{
          x: "max-content",
        }}
        initialSort={sort}
        onSort={setSort}
        onFilter={setFilters}
      />

      <CreateModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onSuccess={() => refetch()}
      />
      <EditModal
        data={editModalData}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onSuccess={() => refetch()}
      />
    </Flex>
  );
};

export default ExpenseTypes;
